/* eslint-disable @typescript-eslint/no-use-before-define */

import { useCallback } from 'react';
import { useQuery } from '@apollo/client';

import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import SuspenseQuery from 'components/SuspenseQuery';
import OBLoaderPage from 'features/Onboarding/components/OBLoaderPage/OBLoaderPage';
import CompanyCompliance from 'features/Onboarding/modules/CompanyCompliance';

import { VerificationStatus } from '../Verification/lib/steps/types';
import { CompanyFinancialInfoDocument } from './graphql/queries/companyFinancialInfo.gql';
import getFinancialInfoStatus from './lib/getHasFinancialInfo';
import machineConfig, { model, State } from './machine';
import FinancialInfo from './pages/FinancialInfo';
import FinancialInfoReview from './pages/FinancialInfoReview';

type CompanyFinancialInfoProps = {
  companyProfileId: string;
};

const CompanyFinancialInfo = asBentoModule<CompanyFinancialInfoProps>(
  ({ companyProfileId, onDone }) => {
    const query = useQuery(CompanyFinancialInfoDocument, {
      variables: {
        companyProfileId,
      },
    });

    return (
      <SuspenseQuery
        loader={<OBLoaderPage onPrev={() => null} />}
        query={query}
      >
        {(companyFinancialInfoData) => (
          <CompanyFinancialInfoComponent
            machineOptions={{
              context: {
                shouldReviewFinancialInfo:
                  getFinancialInfoStatus(
                    companyFinancialInfoData.viewer.company.profile,
                  ) === VerificationStatus.Completed,
              },
            }}
            moduleData={{
              companyFinancialInfoData,
            }}
            onDone={onDone}
          />
        )}
      </SuspenseQuery>
    );
  },
);

const CompanyFinancialInfoComponent = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;

    const { company } = moduleData.companyFinancialInfoData.viewer;

    const onPrev = useCallback(() => send(model.events.PREV()), [send]);

    const stringifiedState = stringifyState<State>(state);

    switch (stringifiedState) {
      case State.FINANCIAL_INFO_REVIEW:
        return (
          <FinancialInfoReview
            companyData={company.profile}
            onEdit={() => send(model.events.EDIT_FINANCIAL_INFO())}
            onPrev={onPrev}
          />
        );

      case State.FINANCIAL_INFO_EDIT:
      case State.SAVE_FINANCIAL_INFO:
        return (
          <FinancialInfo
            companyData={company.profile}
            isSaving={stringifiedState === State.SAVE_FINANCIAL_INFO}
            onContinue={(data) => send(model.events.SAVE_FINANCIAL_INFO(data))}
            onPrev={onPrev}
          />
        );

      case State.COMPLIANCE:
        return (
          <CompanyCompliance
            companyProfileId={company.companyProfileId}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.DONE
                ? send(model.events.NEXT())
                : send(model.events.PREV())
            }
          />
        );

      case State.INIT:
      case State.DONE:
      case State.ABORT:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },
  {
    devTools: true,
    machineConfig,
  },
);

export default CompanyFinancialInfo;
